<template>
  <div id="options">
    <div class="container pt-4">
      <!-- BLOCK SELECTION — a list of all farm blocks with multi-select and
           Select all / Clear, matching the satellite imagery flow. Clicking a
           row toggles the block (and its amber map highlight); clicking a
           block on the map stays in sync. -->
      <div class="block-list-section">
        <h6 class="block-list-header">
          <i class="fas fa-map-marked-alt"></i>
          Blocks
          <span class="block-count">{{ selectedCount }}/{{ allBlocks.length }}</span>
        </h6>
        <div class="multi-actions">
          <button type="button" class="btn-link" @click="selectAll">
            <i class="fas fa-check-double"></i> Select all
          </button>
          <button type="button" class="btn-link" :disabled="selectedCount === 0" @click="clearAll">
            <i class="fas fa-times"></i> Clear
          </button>
          <span class="multi-summary">{{ selectedCount }} selected</span>
        </div>
        <div class="block-list">
          <a
            v-for="block in allBlocks"
            :key="block.properties.blockId"
            class="block-list-item clickable"
            :class="{ 'is-active': isSelected(block) }"
            @click="toggle(block)"
          >
            <span class="block-list-name">
              <i class="multi-check mr-2" :class="isSelected(block) ? 'fas fa-check-square' : 'far fa-square'"></i>
              <strong>{{ block.properties.name || block.properties.blockId }}</strong>
            </span>
          </a>
          <div v-if="!allBlocks.length" class="text-muted small text-center py-3">
            No blocks on this farm yet.
          </div>
        </div>

        <!-- SAMPLE AREA FROM A POLYGON DATASET — lets grids run inside any
             imported polygons (e.g. vineyard-row masks from a drone
             provider), alone or combined with block selections. -->
        <div class="area-dataset-section mt-2">
          <h6 class="block-list-header">
            <i class="fas fa-draw-polygon"></i>
            Or use a polygon dataset as your area
          </h6>
          <div
            v-if="areaDataset"
            class="area-dataset-chip"
          >
            <span class="area-dataset-name">
              <i class="fas fa-check-circle mr-1"></i>
              <strong>{{ areaDataset.name }}</strong>
              <small class="text-muted"> · {{ areaDataset.count }} shape{{ areaDataset.count === 1 ? '' : 's' }}</small>
            </span>
            <i
              class="fas fa-times-circle clickable area-dataset-clear"
              title="Remove this area"
              @click="$store.dispatch('gridGenerator/clearAreaDataset')"
            ></i>
          </div>
          <div v-else>
            <b-form-select
              class="mb-1"
              v-model="areaDatasetChoice"
              :options="areaDatasetOptions"
            ></b-form-select>
            <b-btn
              size="sm"
              variant="outline-primary"
              :disabled="!areaDatasetChoice || $store.state.gridGenerator.loadingAreaDataset"
              @click="$store.dispatch('gridGenerator/useAreaDataset', areaDatasetChoice)"
            >
              <i v-if="$store.state.gridGenerator.loadingAreaDataset" class="fas fa-sync fa-spin"></i>
              <span v-else>Use as sample area</span>
            </b-btn>
          </div>
        </div>

        <p v-if="selectedCount < 1" class="text-danger mt-1 mb-0"><small>Select one or more blocks — or load a polygon dataset — to begin.</small></p>
      </div>

      <!-- Grid settings stay visible regardless of block selection, so
           changing the Grid Base no longer hides the panel or clears blocks. -->
      <div class="mt-3">
        <p class="mb-1"><strong>Grid Base</strong></p>
        <b-form-select
          class="mb-1"
          v-model="gridType"
          :options="gridTypes"
          @change="$store.dispatch('gridGenerator/resetGrid')"
        ></b-form-select>
        <!-- Distributed By Options -->
        <b-form-select class="mb-1" v-if="gridType === 'even'" v-model="gridDistributedBy" :options="gridDistributedByOptions"></b-form-select>
        <!-- Distance -->
        <b-form-select class="mb-1" v-if="gridType === 'even' && gridDistributedBy === 'distance'" v-model="gridDistance" :options="gridDistances"></b-form-select>
        <!-- Number Even Distribution -->
        <b-form-group class="mb-1" v-if="gridType === 'even' && gridDistributedBy === 'count'" description="This is an approximation. Actual grid count may vary slightly.">
          <b-form-input v-model="gridCount" type="number"></b-form-input>
        </b-form-group>
        <!-- Number Random Distribution -->
        <b-form-group class="mb-1" v-if="gridType === 'random'" description="Enter a number of points you'd like randomly generated.">
          <b-form-input v-model="gridCount" type="number"></b-form-input>
        </b-form-group>

        <!-- Dataset Picker -->
        <DatasetPicker v-if="gridType === 'dataset' && !$store.state.gridGenerator.dataset.name" />
        <div v-if="gridType === 'dataset' && $store.state.gridGenerator.dataset.name">
          <!-- SELECTED DATASET -->
          <div
            class="list-group-item list-group-item-action mb-2"
            style="background-color:lightgreen;"
          >
            <p class="lead mb-1">{{ $store.state.gridGenerator.dataset.name }}</p>
            <i
              class="fas fa-times-circle clickable"
              id="remove-dataset"
              @click="removeDataset()"
            ></i>
            <!-- LOADING DATASET SPINNER -->
            <div
              v-if="$store.state.gridGenerator.loadingDataset"
              class="loading-dataset-spinner"
            >
              <RingLoader
                :color="'#61539b'"
                id="spinner"
              ></RingLoader>
            </div>
          </div>
          <p class="mb-1"><strong>Header</strong></p>
          <b-form-group class="mb-1" description="Must select a header to calculate grid locations.">
            <b-form-select v-model="datasetHeader" :options="$store.state.gridGenerator.dataset.headers"></b-form-select>
          </b-form-group>
          <p class="mb-1"><strong>Stratification</strong></p>
          <b-form-group class="mb-1" description="Non-stratified will disperse points evenly across block data. Statified will divide points between defined zones in your Every.Farm account.">
            <b-form-select v-model="datasetModel" :options="datasetModelOptions"></b-form-select>
          </b-form-group>

          <div v-if="datasetModel !== 'manual'">
            <p class="mb-1"><strong>Point Distribution</strong></p>
            <b-form-group class="mb-1" description="Generate points randomly, evenly across values, or using box sampling for optimal zone coverage.">
              <b-form-select v-model="distributionModel" :options="dynamicDistributionModelOptions"></b-form-select>
            </b-form-group>

            <!-- Box Sampling Configuration - Only show for stratified datasets -->
            <div v-if="distributionModel === 'boxSampling' && datasetModel === 'stratified'">
              <p class="mb-1"><strong>Box Sampling Settings</strong><br><small>Discover a represenative sample of your data. Read more in <a href="https://doi.org/10.1007/s11119-025-10225-5" target="_blank" rel="noopener noreferrer">Trivedi et al. (2025)</a></small></p>
              <b-form-group class="mb-1" description="Window size in grid cells (3x3 = 9 points, 5x5 = 25 points, 7x7 = 49 points)">
                <b-form-select v-model="boxSamplingWindowSize" :options="boxSamplingWindowOptions"></b-form-select>
              </b-form-group>
              <b-form-group class="mb-1" description="Minimum data points per zone required in sampling window">
                <b-form-input v-model="minPixelsPerZone" type="number" min="1" max="5"></b-form-input>
              </b-form-group>
              <p v-if="$store.state.gridGenerator.dataset.vizSettings.colorBy !== 'valueBased' || $store.state.gridGenerator.dataset.vizSettings.zoneClassification === 'linear' || $store.state.gridGenerator.dataset.vizSettings.zoneClassification === 'categorical'" class="text-danger">Must setup equal interval or jenks zones in Every.Farm under 'Ramp Settings'!</p>
              <p v-if="$store.state.gridGenerator.dataset.vizSettings.colorBy === 'valueBased' && $store.state.gridGenerator.dataset.vizSettings.zoneClassification !== 'linear' && $store.state.gridGenerator.dataset.vizSettings.zoneClassification !== 'categorical'">
                <strong>Settings from Every.Farm</strong><br>
                Zones: {{ $store.state.gridGenerator.dataset.vizSettings.zoneStops }}<br>
                Classification: {{ $store.state.gridGenerator.dataset.vizSettings.zoneClassification }}
              </p>
            </div>

            <!-- Avoid Edges - Hide for Box Sampling -->
            <b-form-group v-if="distributionModel !== 'boxSampling'" class="mb-1 mt-2" description="The algorithm does its best to avoid generating points on the edges of blocks and zones.">
              <b-form-checkbox
                v-model="avoidEdges"
                name="check-button"
                switch
              >
                Avoid Edges
              </b-form-checkbox>
            </b-form-group>

            <!-- Points per zone - Hide for Box Sampling -->
            <div v-if="datasetModel === 'stratified' && distributionModel !== 'boxSampling'">
              <p v-if="$store.state.gridGenerator.dataset.vizSettings.colorBy !== 'valueBased' || $store.state.gridGenerator.dataset.vizSettings.zoneClassification === 'linear' || $store.state.gridGenerator.dataset.vizSettings.zoneClassification === 'categorical'" class="text-danger">Must setup equal interval or jenks zones in Every.Farm under 'Ramp Settings'!</p>
              <p v-if="$store.state.gridGenerator.dataset.vizSettings.colorBy === 'valueBased' && $store.state.gridGenerator.dataset.vizSettings.zoneClassification !== 'linear' && $store.state.gridGenerator.dataset.vizSettings.zoneClassification !== 'categorical'">
                <strong>Settings from Every.Farm</strong><br>
                Zones: {{ $store.state.gridGenerator.dataset.vizSettings.zoneStops }}<br>
                Classification: {{ $store.state.gridGenerator.dataset.vizSettings.zoneClassification }}
              </p>
              <p class="mb-1"><strong>Points per zone</strong></p>
              <!-- Points per zone -->
              <b-form-group class="mb-1">
                <b-form-input v-model="pointsPerZone" type="number"></b-form-input>
              </b-form-group>
            </div><!-- END Stratified stats -->
            <!-- Number of Stops -->
            <div v-if="datasetModel !== 'stratified'">
              <p class="mb-1"><strong>Number of Points</strong></p>
              <b-form-group class="mb-1">
                <b-form-input v-model="gridCount" type="number"></b-form-input>
              </b-form-group>
            </div>
            <b-btn
              v-if="$store.state.gridGenerator.selectedBlocks.length > 0"
              variant="primary"
              @click="$store.dispatch('gridGenerator/generateDatasetGrid')"
              class="mt-2"
            >Generate Grid</b-btn>
            <!--
            <b-btn
              v-if="gridType == 'even'"
              variant="primary"
              @click="$store.dispatch('gridGenerator/generateEvenGrid')"
              class="mt-1"
            >Generate Grid</b-btn>
            <b-btn
              v-if="gridType == 'random'"
              variant="primary"
              @click="$store.dispatch('gridGenerator/generateRandomGrid')"
              class="mt-1"
            >Generate Grid</b-btn>
            -->
          </div><!-- /not manual modal -->
          <p
            class="text-danger"
            v-if="datasetModel === 'manual'"
          >Please select points manually by clicking them on the map.</p>
        </div><!-- End Dataset settings -->

        <b-btn
          v-if="gridType == 'even' && selectedCount > 0"
          variant="primary"
          @click="$store.dispatch('gridGenerator/generateEvenGrid')"
          class="mt-1"
        >Generate Grid</b-btn>
        <b-btn
          v-if="gridType == 'random' && selectedCount > 0"
          variant="primary"
          @click="$store.dispatch('gridGenerator/generateRandomGrid')"
          class="mt-1"
        >Generate Grid</b-btn>

        <p v-if="$store.state.gridGenerator.gridGeoJson.features" class="mb-1 mt-4"><strong>Use Grid</strong></p>
        <p v-if="$store.state.gridGenerator.gridGeoJson.features">Point Count: {{ $store.state.gridGenerator.gridGeoJson.features.length }}</p>

        <!-- CHOOSE LOCATION -->
        <b-form-group
          v-if="$store.state.gridGenerator.gridGeoJson.features"
          label="New Dataset Name:"
        >
          <b-form-input v-model="post.name"></b-form-input>
        </b-form-group>

        <FolderPicker v-if="$store.state.gridGenerator.gridGeoJson.features" />

        <b-btn
          v-if="$store.state.gridGenerator.gridGeoJson.features"
          @click="$store.dispatch('gridGenerator/download')"
          class="mr-1"
        >Download</b-btn>
        <b-btn
          v-if="$store.state.gridGenerator.gridGeoJson.features && !$store.state.gridGenerator.posting"
          @click="$store.dispatch('gridGenerator/post')"
          variant="success"
        >Add to Every.Farm</b-btn>
        <b-btn
          v-if="$store.state.gridGenerator.posting"
          variant="light"
        ><i class="fas fa-sync fa-spin"></i> Adding now...</b-btn>
      </div><!-- /grid settings -->
    </div>
  </div><!-- /#options -->
</template>

<script>
import DatasetPicker from './DatasetPicker'
import FolderPicker from './FolderPicker'
import RingLoader from 'vue-spinner/src/GridLoader'

const fb = require('@/firebaseConfig.js')

export default {
  data () {
    return {
      // Candidate polygon datasets for the sample-area picker.
      areaDatasetChoice: null,
      areaDatasetCandidates: [],
      datasetModelOptions: [
        { value: 'nonStratified', text: 'Non-Stratified' },
        { value: 'stratified', text: 'Stratified' },
        { value: 'manual', text: 'Manual' }
      ],
      distributionModelOptions: [
        { value: 'random', text: 'Random' },
        { value: 'even', text: 'Even' }
      ],
      gridTypes: [
        { value: 'dataset', text: 'Using a Dataset' },
        { value: 'random', text: 'Random Points Across Blocks' },
        { value: 'even', text: 'Even Points Across Blocks' }
      ],
      gridDistributedByOptions: [
        { value: 'distance', text: 'By Distance' },
        { value: 'count', text: 'By Count' }
      ],
      gridDistances: [
        { value: 1, text: '1 Meter' },
        { value: 3, text: '3 Meters' },
        { value: 5, text: '5 Meters' },
        { value: 10, text: '10 Meters' },
        { value: 30, text: '30 Meters' },
        { value: 100, text: '100 Meters' }
      ],
      boxSamplingWindowOptions: [
        { value: 3, text: '3x3 Window (9 points)' },
        { value: 5, text: '5x5 Window (25 points)' },
        { value: 7, text: '7x7 Window (49 points)' }
      ]
    }
  },
  components: {
    DatasetPicker,
    RingLoader,
    FolderPicker
  },
  computed: {
    pointsPerZone: {
      get () {
        return this.$store.state.gridGenerator.pointsPerZone
      },
      set (value) {
        this.$store.commit('gridGenerator/setStateProperty', { property: 'pointsPerZone', value: value })
      }
    },
    datasetHeader: {
      get () {
        return this.$store.state.gridGenerator.datasetHeader
      },
      set (value) {
        this.$store.commit('gridGenerator/setStateProperty', { property: 'datasetHeader', value: value })
      }
    },
    datasetModel: {
      get () {
        return this.$store.state.gridGenerator.datasetModel
      },
      set (value) {
        this.$store.commit('gridGenerator/setStateProperty', { property: 'datasetModel', value: value })
        // Reset distribution model if switching away from stratified and Box Sampling is selected
        if (value !== 'stratified' && this.$store.state.gridGenerator.distributionModel === 'boxSampling') {
          this.$store.commit('gridGenerator/setStateProperty', { property: 'distributionModel', value: 'random' })
        }
      }
    },
    distributionModel: {
      get () {
        return this.$store.state.gridGenerator.distributionModel
      },
      set (value) {
        this.$store.commit('gridGenerator/setStateProperty', { property: 'distributionModel', value: value })
      }
    },
    post: {
      get () {
        return this.$store.state.gridGenerator.post
      },
      set (value) {
        this.$store.commit('gridGenerator/setStateProperty', { property: 'post', value: value })
      }
    },
    gridType: {
      get () {
        return this.$store.state.gridGenerator.gridType
      },
      set (value) {
        this.$store.commit('gridGenerator/setStateProperty', { property: 'gridType', value: value })
      }
    },
    gridDistance: {
      get () {
        return this.$store.state.gridGenerator.gridDistance
      },
      set (value) {
        this.$store.commit('gridGenerator/setStateProperty', { property: 'gridDistance', value: value })
      }
    },
    gridCount: {
      get () {
        return this.$store.state.gridGenerator.gridCount
      },
      set (value) {
        this.$store.commit('gridGenerator/setStateProperty', { property: 'gridCount', value: value })
      }
    },
    gridDistributedBy: {
      get () {
        return this.$store.state.gridGenerator.gridDistributedBy
      },
      set (value) {
        this.$store.commit('gridGenerator/setStateProperty', { property: 'gridDistributedBy', value: value })
      }
    },
    avoidEdges: {
      get () {
        return this.$store.state.gridGenerator.avoidEdges
      },
      set (value) {
        this.$store.commit('gridGenerator/setStateProperty', { property: 'avoidEdges', value: value })
      }
    },
    boxSamplingWindowSize: {
      get () {
        return this.$store.state.gridGenerator.boxSamplingWindowSize
      },
      set (value) {
        this.$store.commit('gridGenerator/setStateProperty', { property: 'boxSamplingWindowSize', value: value })
      }
    },
    minPixelsPerZone: {
      get () {
        return this.$store.state.gridGenerator.minPixelsPerZone
      },
      set (value) {
        this.$store.commit('gridGenerator/setStateProperty', { property: 'minPixelsPerZone', value: value })
      }
    },
    // Dynamic distribution model options - include Box Sampling only for stratified datasets
    dynamicDistributionModelOptions () {
      const baseOptions = [
        { value: 'random', text: 'Random' },
        { value: 'even', text: 'Even' }
      ]

      // Add Box Sampling option only when stratified is selected
      if (this.datasetModel === 'stratified') {
        baseOptions.push({ value: 'boxSampling', text: 'Box Sampling' })
      }

      return baseOptions
    },
    // All blocks on the farm, sorted by name, for the selectable list.
    allBlocks () {
      const features = (this.$store.state.gridGenerator.blocksGeoJson &&
        this.$store.state.gridGenerator.blocksGeoJson.features) || []
      return features.slice().sort((a, b) => {
        const an = String((a.properties && a.properties.name) || (a.properties && a.properties.blockId) || '').toLowerCase()
        const bn = String((b.properties && b.properties.name) || (b.properties && b.properties.blockId) || '').toLowerCase()
        return an.localeCompare(bn)
      })
    },
    // Set of selected blockIds for O(1) membership checks in the list.
    selectedBlockIds () {
      return new Set(this.$store.state.gridGenerator.selectedBlocks.map(b => b.properties.blockId))
    },
    selectedCount () {
      return this.$store.state.gridGenerator.selectedBlocks.length
    },
    areaDataset () {
      return this.$store.state.gridGenerator.areaDataset
    },
    areaDatasetOptions () {
      const opts = this.areaDatasetCandidates.map(d => ({ value: d, text: d.name }))
      return [{ value: null, text: 'Choose a dataset…' }].concat(opts)
    }
  },
  created () {
    this.fetchAreaDatasetCandidates()
  },
  watch: {
    // The plugin can mount before the farm finishes loading (created()'s
    // fetch bails without a farmId) — refetch when the farm arrives so the
    // area picker isn't permanently empty after a slow entry.
    '$store.state.gridGenerator.farm.id' (id) {
      if (id && !this.areaDatasetCandidates.length) this.fetchAreaDatasetCandidates()
    }
  },
  methods: {
    // Non-raster GeoJSON datasets on this farm, newest first. Whether a
    // dataset actually contains polygons is checked at load time by
    // useAreaDataset (the doc doesn't record geometry type).
    async fetchAreaDatasetCandidates () {
      try {
        const farmId = this.$store.state.gridGenerator.farm.id || this.$store.state.farm.id
        if (!farmId) return
        // Single-equality query so no composite index is needed (the
        // farmId+updatedOn pair isn't guaranteed to have one); sort here.
        const snap = await fb.datasetsCollection
          .where('farmId', '==', farmId)
          .limit(200)
          .get()
        this.areaDatasetCandidates = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(d => d.file && (d.file.geoJsonPath || d.file.path) && !d.file.raster && !d.trashed)
          .sort((a, b) => {
            const at = (a.updatedOn && a.updatedOn.seconds) || 0
            const bt = (b.updatedOn && b.updatedOn.seconds) || 0
            return bt - at
          })
      } catch (e) {
        console.log('Area dataset list error:', e)
      }
    },
    isSelected (block) {
      return this.selectedBlockIds.has(block.properties.blockId)
    },
    toggle (block) {
      this.$store.dispatch('gridGenerator/toggleBlock', block)
    },
    selectAll () {
      this.$store.dispatch('gridGenerator/selectAllBlocks')
    },
    clearAll () {
      this.$store.dispatch('gridGenerator/clearBlocks')
    },
    removeDataset () {
      this.$store.commit('gridGenerator/setStateProperty', { property: 'dataset', value: {} })
      const mapObject = this.$store.state.gridGenerator.mapObject
      if (mapObject.getLayer('datasetPreview')) {
        mapObject.removeLayer('datasetPreview')
        mapObject.removeSource('datasetPreview')
      }
    }
  }
}
</script>

<style scoped>
#options {
  position: absolute;
  top: 56px;
  left: 0;
  height: calc(100% - 56px);
  width: 300px;
  z-index: 1;
  overflow-y: scroll;
  padding-bottom: 50px;
  background: #fff;
  border-right: 1px solid #d6dbe0;
}

/* Block selection list — mirrors the satellite imagery feature's pattern
   (scrollable list, multi-select rows, Select all / Clear, count badge). */
.block-list-section {
  margin-bottom: 0.5rem;
}
.block-list-header {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;
  color: #495057;
  margin-bottom: 0.4rem;
}
.block-count {
  margin-left: auto;
  font-size: 0.7rem;
  background: #f1f3f5;
  color: #6c757d;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 600;
}
.multi-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 2px 2px 6px;
  font-size: 0.78rem;
}
.multi-actions .btn-link {
  background: none;
  border: none;
  color: #6e59c7;
  cursor: pointer;
  padding: 0;
  font-size: 0.78rem;
}
.multi-actions .btn-link:disabled {
  color: #adb5bd;
  cursor: not-allowed;
}
.multi-actions .btn-link:hover:not(:disabled) {
  text-decoration: underline;
}
.multi-actions .multi-summary {
  margin-left: auto;
  color: #6c757d;
}
.block-list {
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid #e5e9f0;
  border-radius: 6px;
  background: #fafbfc;
}
.block-list-item {
  display: block;
  padding: 0.5rem 0.7rem;
  border-bottom: 1px solid #eef1f4;
  color: #212529;
  text-decoration: none;
  font-size: 0.88rem;
  cursor: pointer;
  transition: background 0.1s;
}
.block-list-item:last-child { border-bottom: none; }
.block-list-item:hover { background: #f0edfa; }
.block-list-item.is-active {
  background: #f0edfa;
  border-left: 3px solid #6e59c7;
  padding-left: calc(0.7rem - 3px);
}
.block-list-name { line-height: 1.2; }
.multi-check { color: #6e59c7; }

/* Sample-area-from-dataset picker */
.area-dataset-section {
  padding-top: 0.25rem;
}
.area-dataset-chip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: #fff8e6;
  border: 1px solid #ffc107;
  border-radius: 6px;
  padding: 0.45rem 0.7rem;
  font-size: 0.85rem;
}
.area-dataset-clear {
  color: #6c757d;
}
.area-dataset-clear:hover {
  color: #a33d2f;
}

#remove-dataset {
  position: absolute;
  top: 10px;
  right: 10px;
}

.loading-dataset-spinner {
  position: absolute;
  top: 0;
  left: 0;
  background-color: rgba(255,255,255,0.3);
  width: 100%;
  height: 100%;
}
.v-spinner {
  position: absolute;
  left: calc(50% - 30px);
  top: 16px;
}

@media (max-width: 600px) {
  #options {
    top: 60%;
    height: 40%;
    width: 100%;
  }
}
</style>
